import { useEffect, useState } from 'react';
import type { Bricks } from '../components/Table';
import { PlayerArea } from '../components/PlayerArea';
import type { SeatView, ZoneView } from '../model/table';
import { parseSelector, selectCounters, selectZones } from './selectors.ts';
import type { LayoutNode, Presentation } from './types.ts';

const NARROW = '(max-width: 899px)';

function useNarrow(): boolean {
  const [narrow, setNarrow] = useState(() => typeof window !== 'undefined' && window.matchMedia(NARROW).matches);
  useEffect(() => {
    const query = window.matchMedia(NARROW);
    const listener = () => setNarrow(query.matches);
    query.addEventListener('change', listener);
    return () => query.removeEventListener('change', listener);
  }, []);
  return narrow;
}

interface Walk { placed: Set<string>; components: Set<string>; hasRest: boolean }

/** Collects the zones a layout places (other than `rest`) and the components it uses. */
function walk(node: LayoutNode, bricks: Bricks, seat: string | null, acc: Walk): void {
  const self = bricks.table.selfSeat;
  switch (node.type) {
    case 'row': case 'column': case 'grid': node.children.forEach(c => walk(c, bricks, seat, acc)); break;
    case 'tabs': node.tabs.forEach(t => walk(t.child, bricks, seat, acc)); break;
    case 'panel': walk(node.child, bricks, seat, acc); break;
    case 'repeat': for (const s of seatsFor(node, bricks)) walk(node.child, bricks, s.id, acc); break;
    case 'component': {
      acc.components.add(node.component);
      if (!node.select) break;
      const sel = parseSelector(node.select);
      if (typeof sel === 'string') break;
      if (sel.kind === 'rest') { acc.hasRest = true; break; }
      for (const z of selectZones(sel, bricks.table.zones, seat, self, acc.placed)) acc.placed.add(z.id);
    }
  }
}

function seatsFor(node: Extract<LayoutNode, { type: 'repeat' }>, bricks: Bricks): SeatView[] {
  let seats = bricks.table.seats;
  if (node.over === 'opponents') seats = seats.filter(s => !s.isSelf);
  if (node.order === 'self-first') seats = [...seats.filter(s => s.isSelf), ...seats.filter(s => !s.isSelf)];
  return seats;
}

export function LayoutRenderer({ presentation, bricks }: { presentation: Presentation; bricks: Bricks }) {
  const narrow = useNarrow();
  const root = (narrow && presentation.layouts.layouts.narrow) || presentation.layouts.layouts.wide;
  const acc: Walk = { placed: new Set(), components: new Set(), hasRest: false };
  walk(root, bricks, null, acc);
  const unplaced = bricks.table.zones.filter(z => !acc.placed.has(z.id));
  // A presentation can arrange information but never hide it or make a decision unreachable.
  const missingSide = (['choices', 'inspector', 'status'] as const).filter(c => !acc.components.has(c));
  return (
    <div className={`layout-root${missingSide.length ? ' layout-with-side' : ''}`}>
      <div className="layout-main">
        {missingSide.includes('status') && bricks.status()}
        <Node node={root} bricks={bricks} seat={null} placed={acc.placed} />
        {!acc.hasRest && unplaced.length > 0 && (
          <details className="panel more-panel">
            <summary className="panel-title">More</summary>
            <div className="layout-column gap-m">{unplaced.map(z => (z.kind === 'board' && z.boardId ? bricks.board(z) : bricks.zone(z)))}</div>
          </details>
        )}
      </div>
      {(missingSide.includes('choices') || missingSide.includes('inspector')) && (
        <aside className="layout-side table-side" aria-label="Moves and details">
          {missingSide.includes('choices') && bricks.choices()}
          {missingSide.includes('inspector') && bricks.inspector()}
        </aside>
      )}
    </div>
  );
}

function Node({ node, bricks, seat, placed, inPanel = false }: { node: LayoutNode; bricks: Bricks; seat: string | null; placed: Set<string>; inPanel?: boolean }): React.ReactNode {
  switch (node.type) {
    case 'row': case 'column': {
      const weights = node.weights;
      return (
        <div className={`layout-${node.type} gap-${node.gap ?? 'm'}`}>
          {node.children.map((child, i) => (
            <div key={i} className="layout-cell" style={weights ? { flex: `${weights[i]} 1 0` } : undefined}>
              <Node node={child} bricks={bricks} seat={seat} placed={placed} />
            </div>
          ))}
        </div>
      );
    }
    case 'grid':
      return (
        <div className="layout-grid" style={{ gridTemplateColumns: `repeat(${node.columns}, minmax(0, 1fr))` }}>
          {node.children.map((child, i) => <div key={i} className="layout-cell"><Node node={child} bricks={bricks} seat={seat} placed={placed} /></div>)}
        </div>
      );
    case 'tabs': return <Tabs node={node} bricks={bricks} seat={seat} placed={placed} />;
    case 'panel': {
      const body = <Node node={node.child} bricks={bricks} seat={seat} placed={placed} inPanel={!!node.title} />;
      if (node.collapsible) return <details className="panel" open><summary className="panel-title">{node.title ?? 'Details'}</summary>{body}</details>;
      return <section className="panel" aria-label={node.title}>{node.title && <h2 className="panel-title">{node.title}</h2>}{body}</section>;
    }
    case 'repeat':
      return (
        <div className="layout-repeat">
          {seatsFor(node, bricks).map(s => (
            <PlayerArea key={s.id} seat={s} seatIndex={bricks.seatIndex} label={s.isSelf ? 'Your area' : s.label}>
              <Node node={node.child} bricks={bricks} seat={s.id} placed={placed} />
            </PlayerArea>
          ))}
        </div>
      );
    case 'component': return <Component node={node} bricks={bricks} seat={seat} placed={placed} inPanel={inPanel} />;
  }
}

function Tabs({ node, bricks, seat, placed }: { node: Extract<LayoutNode, { type: 'tabs' }>; bricks: Bricks; seat: string | null; placed: Set<string> }) {
  const [active, setActive] = useState(0);
  const current = node.tabs[active] ?? node.tabs[0]!;
  return (
    <div className="layout-tabs">
      <div role="tablist" className="tab-list">
        {node.tabs.map((t, i) => (
          <button key={t.label} role="tab" type="button" aria-selected={i === active} className="tab" onClick={() => setActive(i)}>{t.label}</button>
        ))}
      </div>
      <div role="tabpanel"><Node node={current.child} bricks={bricks} seat={seat} placed={placed} /></div>
    </div>
  );
}

function Component({ node, bricks, seat, placed, inPanel }: { node: Extract<LayoutNode, { type: 'component' }>; bricks: Bricks; seat: string | null; placed: Set<string>; inPanel: boolean }) {
  const props = (node.props ?? {}) as Record<string, unknown>;
  const self = bricks.table.selfSeat;
  const selector = node.select ? parseSelector(node.select) : null;
  const sel = typeof selector === 'string' ? null : selector;
  const zones = (): ZoneView[] => (sel ? selectZones(sel, bricks.table.zones, seat, self, placed) : []);
  switch (node.component) {
    case 'zone':
      return <>{zones().map(z => bricks.zone(z, {
        display: props.display as never, columns: props.columns as number | undefined, size: props.size as never,
        showEmpty: props.showEmpty as boolean | undefined, bare: inPanel && zones().length === 1,
      }))}</>;
    case 'board': return <>{zones().filter(z => z.kind === 'board').map(z => bricks.board(z, inPanel, props.zoom as boolean | undefined))}</>;
    case 'counters': {
      const counters = sel ? selectCounters(sel, bricks.table.counters, seat, self) : [];
      return <>{bricks.counters(counters, seat ? `${seat} counters` : 'Counters', props.style as never)}</>;
    }
    case 'seat-header': {
      const s = bricks.table.seats.find(x => x.id === seat);
      return s ? <>{bricks.seatHeader(s)}</> : null;
    }
    case 'status': return <>{bricks.status()}</>;
    case 'choices': return <>{bricks.choices()}</>;
    case 'inspector': return <>{bricks.inspector()}</>;
    case 'log': return <>{bricks.log(props.limit as number | undefined)}</>;
    case 'result': return <>{bricks.result()}</>;
  }
}
